import { adminApi } from '@core/api/httpClient'
import type {
  CreateFeeDefinitionPayload,
  GetFeesMasterParams,
  SupersedeFeeDefinitionPayload,
} from './feesMasterContracts'
import { parseFeeDefinitionList, parseFeeDefinitionRow } from './feesMasterParser'
import type { FeeDefinition, FeeDefinitionList, FeeDefinitionVersion } from '../types/feeTypes'

const BASE = '/api/admin/fees/master'

/** Only the versions in force (`valid_to IS NULL`), one per fee code. */
export async function getFeesMaster(params: GetFeesMasterParams = {}): Promise<FeeDefinitionList> {
  const { data } = await adminApi.get<unknown>(BASE, { params })
  return parseFeeDefinitionList(data)
}

export async function getFeeDefinition(id: string): Promise<FeeDefinition> {
  const { data } = await adminApi.get<unknown>(`${BASE}/${id}`)
  return parseFeeDefinitionRow(data)
}

/** Every version of the fee this row belongs to, newest first. */
export async function getFeeDefinitionVersions(id: string): Promise<FeeDefinitionVersion[]> {
  const { data } = await adminApi.get<unknown>(`${BASE}/${id}/versions`)
  return parseFeeDefinitionList(data).rows
}

export async function createFeeDefinition(
  payload: CreateFeeDefinitionPayload,
): Promise<FeeDefinition> {
  const { data } = await adminApi.post<unknown>(BASE, payload)
  return parseFeeDefinitionRow(data)
}

/**
 * Closes the version in force and opens a new one carrying the merged values.
 * Returns the superseding row, whose id differs from the one passed in.
 */
export async function supersedeFeeDefinition(
  id: string,
  payload: SupersedeFeeDefinitionPayload,
): Promise<FeeDefinition> {
  const { data } = await adminApi.patch<unknown>(`${BASE}/${id}`, payload)
  return parseFeeDefinitionRow(data)
}

/**
 * Deactivates the definition. Never a hard delete — `property_fees` rows
 * reference it, and a settled fee has to stay traceable to its definition.
 */
export async function disableFeeDefinition(id: string): Promise<void> {
  await adminApi.delete(`${BASE}/${id}`)
}
